import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, ArrowRight, ShieldAlert, BookOpen, Phone, MessageSquare } from 'lucide-react';
import { siteConfig } from '../data';
import { formatDanishDate } from '../lib/utils';

export default function Vidensbank() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Alle');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        let res;
        try {
          res = await fetch('/_api/public/content/vidensbank_articles');
          if (!res.ok) throw new Error('not ok');
        } catch {
          res = await fetch('https://api.dev.auxo.dk/public/content/vidensbank_articles');
        }
        if (!res.ok) {
          throw new Error('Kunne ikke hente artikler fra CMS');
        }
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.items || [];
        // Sort descending by published_date so newest (e.g. 2026-10-05) is first / top-left
        list.sort((a, b) => (b.published_date || '').localeCompare(a.published_date || ''));
        setArticles(list);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const getSlug = (item) => {
    if (!item.slug) return '';
    return typeof item.slug === 'object' ? item.slug._ : item.slug;
  };

  // Extract unique categories
  const categories = ['Alle', ...new Set(articles.map((a) => a.category).filter(Boolean))];

  // Filter articles based on search and category
  const filteredArticles = articles.filter((article) => {
    const matchesCategory = selectedCategory === 'Alle' || article.category === selectedCategory;
    const matchesSearch = 
      article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.summary?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="bg-slate-50 min-h-screen py-12 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Hero Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center px-3 py-1 rounded-none text-xs font-semibold bg-blue-100 text-[#103B66] mb-4">
            <BookOpen className="w-3.5 h-3.5 mr-1.5" />
            Vidensbank
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-[#103B66] tracking-tight mb-6">
            Forebyggelse af onlinesvindel
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Her samler vi gode råd, guides og artikler, der hjælper dig med at færdes sikkert på internettet. Læs vores letforståelige vejledninger og undgå at blive snydt.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-none p-6 shadow-sm border border-slate-100 mb-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Søg i artikler..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-none focus:outline-none focus:ring-2 focus:ring-[#103B66] focus:bg-white transition-all text-slate-800"
              />
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2.5 rounded-none text-sm font-semibold transition-all ${
                    selectedCategory === category
                      ? 'bg-[#103B66] text-white shadow-md shadow-blue-900/10'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-100 border-t-[#103B66] rounded-full animate-spin mb-4"></div>
            <p className="text-slate-500 font-medium">Henter artikler fra Vidensbanken...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-100 rounded-none p-8 text-center max-w-xl mx-auto">
            <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-red-900 mb-2">Der skete en fejl</h3>
            <p className="text-red-700 mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-red-600 text-white font-semibold rounded-none hover:bg-red-700 transition-colors"
            >
              Prøv igen
            </button>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="bg-white rounded-none border border-slate-100 p-12 text-center max-w-md mx-auto">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-800 mb-1">Ingen artikler fundet</h3>
            <p className="text-slate-500 mb-6">Vi kunne ikke finde nogen artikler, der matcher din søgning eller filter.</p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('Alle'); }}
              className="px-5 py-2 bg-slate-100 text-slate-700 font-semibold rounded-none hover:bg-slate-200 transition-colors"
            >
              Nulstil søgning
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => {
              const slug = getSlug(article);
              const imageUrl = article.image?.[0] || 'https://assets.auxodev.dk/assets/2cadb5d8-c2c5-4850-ae22-0167799adce5/6fa6bf9e-bc8b-4301-b93b-b252cb898cf6.jpg';
              
              return (
                <article 
                  key={article.id} 
                  className="bg-white rounded-none border border-slate-100 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-all group"
                >
                  {/* Image */}
                  <div className="aspect-video w-full overflow-hidden bg-slate-100 relative">
                    <img 
                      src={imageUrl} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-slate-800 text-xs font-bold px-3 py-1.5 rounded-none shadow-sm">
                      {article.category}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="p-6 flex-grow flex flex-col">
                    <div className="flex items-center text-xs text-slate-400 font-medium mb-3">
                      <Calendar className="w-3.5 h-3.5 mr-1.5" />
                      {formatDanishDate(article.published_date)}
                    </div>
                    
                    <h3 className="text-xl font-bold text-[#103B66] mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {article.title}
                    </h3>
                    
                    <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3 flex-grow">
                      {article.summary}
                    </p>

                    <Link
                      to={`/vidensbank/${slug}`}
                      className="inline-flex items-center text-sm font-bold text-[#103B66] hover:text-blue-600 transition-colors mt-auto group/btn"
                    >
                      Læs hele artiklen
                      <ArrowRight className="w-4 h-4 ml-1.5 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Acute Help Banner */}
        <div className="mt-20 bg-gradient-to-br from-[#103B66] to-slate-900 rounded-none p-8 md:p-12 text-white shadow-xl relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_50%)]" />
          <div className="relative z-10 max-w-3xl">
            <span className="inline-flex items-center px-3 py-1 rounded-none text-xs font-semibold bg-[#c80306]/20 text-[#c80306] border border-[#c80306]/30 mb-4">
              <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
              Akut hjælp mod svindel
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Har du mistanke om, at du er blevet svindlet?
            </h2>
            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
              Tøv ikke med at kontakte os med det samme. Vi hjælper dig med at bevare roen, kontakte banken, spærre MitID og sikre dine enheder mod yderligere misbrug.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-6 py-3.5 bg-white text-[#103B66] font-bold rounded-none hover:bg-slate-100 transition-all shadow-lg active:scale-95"
              >
                <Phone className="w-5 h-5 mr-2" />
                Ring til os
              </a>
              <a
                href={`sms:${siteConfig.sms.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-6 py-3.5 bg-white/10 text-white font-bold rounded-none hover:bg-white/20 transition-all border border-white/20 active:scale-95"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Send en SMS
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
